import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useFetchTaskHistory } from '../state/data/task';
import { completionLineOf, taskDisplayTitle } from '../logic/task';

const PRIO_COLORS = {
  urgent: { bg: 'var(--wi-background-bad)', color: 'var(--wi-status-bad)' },
  high:   { bg: 'var(--wi-background-moderate)', color: 'var(--wi-text-primary-moderate)' },
  normal: { bg: 'var(--wi-background-primary)', color: 'var(--wi-text-secondary)' },
  low:    { bg: 'var(--wi-background-good)', color: 'var(--wi-text-primary-good)' },
};

const STATUS_STYLES = {
  completed:   { bg: 'var(--wi-background-good)', color: 'var(--wi-text-primary-good)', label: 'Completed' },
  in_progress: { bg: 'var(--wi-background-info)', color: 'var(--wi-action-primary)',     label: 'In Progress' },
  pending:     { bg: 'var(--wi-background-moderate)', color: 'var(--wi-text-primary-moderate)', label: 'Pending' },
  // Both are real stored statuses; without them the fallback rendered them as "Pending".
  // Values copied verbatim from TaskCalendarModal so one status never has two colours.
  scheduled:   { bg: 'var(--wi-background-primary)', color: 'var(--wi-text-secondary)',  label: 'Scheduled' },
  overdue:     { bg: 'var(--wi-background-bad)', color: 'var(--wi-status-bad)',          label: 'Overdue' },
  cancelled:   { bg: 'var(--wi-background-primary)', color: 'var(--wi-text-secondary)',  label: 'Cancelled' },
  missed:      { bg: 'var(--wi-background-bad)', color: 'var(--wi-text-primary-bad)',    label: 'Missed' },
};

const DOMAIN_COLORS = {
  bp: '#1F4AC2', weight: '#059669', lab: '#7C3AED',
  medication: '#D97706', reporting: '#0284C7',
  compliance: '#BE185D', outreach: '#6B7280', device: '#374151',
};

function formatDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const PRIORITY_ORDER = { urgent: 0, high: 1, normal: 2, low: 3 };

function TaskRow({ task }) {
  const [expanded, setExpanded] = useState(false);
  const prio = PRIO_COLORS[task.priority] || PRIO_COLORS.normal;
  const status = STATUS_STYLES[task.status] || STATUS_STYLES.pending;
  const isFaded = task.status === 'cancelled' || task.status === 'missed';

  const line = completionLineOf(task);
  const completionByLine = line?.by ?? null;
  const dateLabel = line?.at
    ? `${line.verb} ${formatDate(line.at)}`
    : task.completed_at
      ? `Completed ${formatDate(task.completed_at)}`
      : task.due_at
      ? `Due ${formatDate(task.due_at)}`
      : (task.created ?? task.created_at)
        ? `Created ${formatDate(task.created ?? task.created_at)}`
        : null;

  return (
    <div
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: '10px 16px',
        borderBottom: '1px solid var(--wi-divider)',
        opacity: isFaded ? 0.6 : 1,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span className="body2" style={{
            fontWeight: 500,
            textDecoration: task.status === 'cancelled' ? 'line-through' : 'none',
          }}>
            {taskDisplayTitle(task)}
          </span>
          {(task.priority === 'urgent' || task.priority === 'high') && (
            <span style={{
              fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20,
              background: prio.bg, color: prio.color,
              textTransform: 'uppercase', letterSpacing: '0.02em',
            }}>
              {task.priority}
            </span>
          )}
          {task.clinical_domain && (
            <span style={{
              fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 20,
              background: `${DOMAIN_COLORS[task.clinical_domain] || '#6B7280'}18`,
              color: DOMAIN_COLORS[task.clinical_domain] || '#6B7280',
            }}>
              {task.clinical_domain}
            </span>
          )}
        </div>
        {task.description && (() => {
          const isLong = task.description.length > 100;
          return (
            <p
              className="detail1 text-secondary"
              onClick={isLong ? () => setExpanded(v => !v) : undefined}
              style={{ margin: '3px 0 0', lineHeight: 1.4, cursor: isLong ? 'pointer' : 'default' }}
            >
              {isLong && !expanded ? task.description.slice(0, 100) + '\u2026' : task.description}
              {isLong && (
                <span style={{ marginLeft: 6, color: 'var(--wi-action-primary)', fontWeight: 500 }}>
                  {expanded ? 'Less' : 'More'}
                </span>
              )}
            </p>
          );
        })()}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
        <span style={{
          fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20,
          background: status.bg, color: status.color,
          textTransform: 'uppercase', letterSpacing: '0.02em',
        }}>
          {status.label}
        </span>
        {dateLabel && (
          <span className="detail1 text-secondary" style={{ fontSize: 11 }}>{dateLabel}</span>
        )}
        {completionByLine && (
          <span className="detail1 text-secondary" style={{ fontSize: 10 }}>{completionByLine}</span>
        )}
      </div>
    </div>
  );
}

const selectStyle = {
  background: 'var(--wi-background-primary)',
  border: '1px solid var(--wi-divider)',
  borderRadius: 6,
  padding: '4px 8px',
  fontSize: 12,
  fontFamily: 'var(--wi-font-family)',
  color: 'var(--wi-text-primary)',
  cursor: 'pointer',
};

export default function TaskHistoryModal({ patient, onClose }) {
  // Two-tier loading: open tasks ALWAYS fully loaded (safety guarantee),
  // closed history paginated. `data === null` = initial loading.
  const [data, setData] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDomain, setFilterDomain] = useState('');
  // 'staff' default: patient-assigned tasks are clutter for the care team
  // view. Switch to 'patient' or '' (any) to broaden.
  const [filterAssignee, setFilterAssignee] = useState('staff');
  const [sortBy, setSortBy] = useState('open_first');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchTaskHistory = useFetchTaskHistory();

  // Server-side filter: re-fetch and reset pagination whenever assignee changes.
  useEffect(() => {
    setData(null);
    fetchTaskHistory(patient.userid, {
      assignedToType: filterAssignee || undefined,
    }).then(setData);
  }, [patient.userid, filterAssignee, fetchTaskHistory]);

  async function handleLoadMore() {
    if (!data?.hasMoreClosed || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await fetchTaskHistory(patient.userid, {
        closedOffset: data.closedOffset + data.closedTasks.length,
        closedLimit: data.closedLimit,
        assignedToType: filterAssignee || undefined,
      });
      setData(prev => ({
        ...prev,
        closedTasks: [...prev.closedTasks, ...next.closedTasks],
        closedTotal: next.closedTotal,
        closedOffset: prev.closedOffset,
        hasMoreClosed: next.hasMoreClosed,
      }));
    } finally {
      setLoadingMore(false);
    }
  }

  async function handleLoadAll() {
    if (!data?.hasMoreClosed || loadingMore) return;
    setLoadingMore(true);
    try {
      let acc = [...data.closedTasks];
      let offset = data.closedOffset + acc.length;
      let total = data.closedTotal;
      let more = data.hasMoreClosed;
      while (more) {
        const next = await fetchTaskHistory(patient.userid, {
          closedOffset: offset,
          closedLimit: data.closedLimit,
          assignedToType: filterAssignee || undefined,
        });
        acc = [...acc, ...next.closedTasks];
        offset += next.closedTasks.length;
        total = next.closedTotal;
        more = next.hasMoreClosed;
      }
      setData(prev => ({
        ...prev,
        closedTasks: acc,
        closedTotal: total,
        hasMoreClosed: false,
      }));
    } finally {
      setLoadingMore(false);
    }
  }

  // Disable body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Close on Escape
  useEffect(() => {
    function handleKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Combined view for sort/filter — open tasks (full set) + currently-loaded
  // closed history. Open tasks are always present; only closed history is bounded.
  const tasks = useMemo(() => {
    if (!data) return [];
    return [...(data.openTasks || []), ...(data.closedTasks || [])];
  }, [data]);
  const openCount = data?.openTasks?.length ?? 0;
  const closedLoaded = data?.closedTasks?.length ?? 0;
  const closedTotal = data?.closedTotal ?? 0;
  const hasMore = data?.hasMoreClosed || false;

  // Extract unique domains for filter dropdown
  const domains = useMemo(() => {
    const set = new Set(tasks.map(t => t.clinical_domain).filter(Boolean));
    return [...set].sort();
  }, [tasks]);

  // Apply filters + sort
  const filtered = useMemo(() => {
    let result = tasks;
    if (filterStatus === 'active') {
      result = result.filter(t => ['pending', 'in_progress', 'overdue', 'scheduled'].includes(t.status));
    } else if (filterStatus) {
      result = result.filter(t => t.status === filterStatus);
    }
    if (filterDomain) {
      result = result.filter(t => t.clinical_domain === filterDomain);
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      result = result.filter(t => {
        const d = new Date(t.completed_at || t.due_at || t.created || t.created_at);
        return d >= from;
      });
    }
    if (dateTo) {
      const to = new Date(dateTo + 'T23:59:59');
      result = result.filter(t => {
        const d = new Date(t.completed_at || t.due_at || t.created || t.created_at);
        return d <= to;
      });
    }
    // Sort
    const OPEN_STATUSES = new Set(['pending', 'in_progress', 'overdue', 'scheduled']);
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'open_first': {
          // Group: opens (0) before closed (1). Within each group, most recently
          // updated first, so a freshly-created/touched open task rises to the top.
          const ga = OPEN_STATUSES.has(a.status) ? 0 : 1;
          const gb = OPEN_STATUSES.has(b.status) ? 0 : 1;
          if (ga !== gb) return ga - gb;
          return new Date(b.modified ?? b.updated_at ?? 0) - new Date(a.modified ?? a.updated_at ?? 0);
        }
        case 'chronological':
          return new Date(a.created ?? a.created_at ?? 0) - new Date(b.created ?? b.created_at ?? 0);
        case 'updated':
          return new Date(b.modified ?? b.updated_at ?? 0) - new Date(a.modified ?? a.updated_at ?? 0);
        case 'due':
          return new Date(a.due_at || '9999') - new Date(b.due_at || '9999');
        case 'priority':
          return (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9);
        case 'created':
          return new Date(b.created ?? b.created_at ?? 0) - new Date(a.created ?? a.created_at ?? 0);
        case 'status': {
          const order = { in_progress: 0, pending: 1, completed: 2, cancelled: 3, missed: 4 };
          return (order[a.status] ?? 9) - (order[b.status] ?? 9);
        }
        default:
          return 0;
      }
    });
    return result;
  }, [tasks, filterStatus, filterDomain, dateFrom, dateTo, sortBy]);

  return createPortal((
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 720, width: '90vw' }}
      >
        {/* Header */}
        <div className="modal-header">
          <div>
            <h2 className="header3" style={{ margin: 0 }}>Task History</h2>
            <p className="detail1 text-secondary" style={{ margin: '4px 0 0', textTransform: 'capitalize' }}>
              {patient.firstname} {patient.lastname}
              {data && (
                <>
                  {' '}&mdash; <strong style={{ color: 'var(--wi-text-primary)' }}>{openCount} open</strong>
                  {' · '}{closedLoaded === closedTotal
                    ? `${closedTotal} closed`
                    : `${closedLoaded} of ${closedTotal} closed loaded`}
                </>
              )}
            </p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        {/* Filters */}
        {data && tasks.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={selectStyle}>
                <option value="open_first">Open first (recent)</option>
                <option value="updated">Recently updated</option>
                <option value="chronological">Chronological</option>
                <option value="due">Due date</option>
                <option value="priority">Priority</option>
                <option value="created">Newest first</option>
                <option value="status">Status</option>
              </select>
              <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)} style={selectStyle}>
                <option value="staff">Care team tasks</option>
                <option value="patient">Patient tasks</option>
                <option value="">All assignees</option>
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selectStyle}>
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="missed">Missed</option>
              </select>
              <select value={filterDomain} onChange={e => setFilterDomain(e.target.value)} style={selectStyle}>
                <option value="">All domains</option>
                {domains.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className="detail1 text-secondary" style={{ fontSize: 11 }}>From</span>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ ...selectStyle, width: 130 }} />
              <span className="detail1 text-secondary" style={{ fontSize: 11 }}>To</span>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ ...selectStyle, width: 130 }} />
              {(dateFrom || dateTo) && (
                <button
                  onClick={() => { setDateFrom(''); setDateTo(''); }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    fontSize: 12, color: 'var(--wi-action-primary)', fontFamily: 'var(--wi-font-family)', fontWeight: 500,
                  }}
                >
                  Clear dates
                </button>
              )}
              {(filterStatus || filterDomain || dateFrom || dateTo) && (
                <span className="detail1 text-secondary" style={{ marginLeft: 'auto' }}>
                  {filtered.length} of {tasks.length}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Loading */}
        {data === null && (
          <div className="loading-center" style={{ padding: '40px 0' }}>
            <div className="loading-spinner" />
            <span className="loading-center-text">Loading task history…</span>
          </div>
        )}

        {/* Empty */}
        {data && tasks.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p className="body2 text-secondary">No tasks found for this patient</p>
          </div>
        )}

        {/* Filtered empty */}
        {data && tasks.length > 0 && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <p className="body2 text-secondary">No tasks match the selected filters</p>
          </div>
        )}

        {/* Task list */}
        {filtered.length > 0 && (
          <div className="table-component" style={{ maxHeight: '55vh', overflow: 'auto' }}>
            {filtered.map(t => <TaskRow key={t.task_id} task={t} />)}
            {/* Inline pagination footer — only relevant for closed history.
                Hidden when the user filters to active-only since opens are
                already fully loaded; loading more wouldn't change the view. */}
            {hasMore && filterStatus !== 'active' && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 12, padding: '12px 16px', borderTop: '1px solid var(--wi-divider)',
              }}>
                <span className="detail1 text-secondary">
                  {closedLoaded} of {closedTotal} closed tasks loaded
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    style={{
                      background: 'var(--wi-background-primary)', border: '1px solid var(--wi-divider)',
                      borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 500,
                      cursor: loadingMore ? 'wait' : 'pointer', color: 'var(--wi-text-primary)',
                      fontFamily: 'var(--wi-font-family)',
                    }}
                  >
                    {loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                  <button
                    onClick={handleLoadAll}
                    disabled={loadingMore}
                    style={{
                      background: 'none', border: 'none', cursor: loadingMore ? 'wait' : 'pointer',
                      padding: '4px 8px', fontSize: 12, fontWeight: 500,
                      color: 'var(--wi-action-primary)', fontFamily: 'var(--wi-font-family)',
                    }}
                  >
                    Load all
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  ), document.body);
}
